import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import VisitDetail from './pages/VisitDetail';
import Notifications from './pages/Notifications';
import Clinics from './pages/Clinics';
import Reports from './pages/Reports';
import Appointments from './pages/Appointments';
import Health from './pages/Health';
import Settings from './pages/Settings';
import PortalSwitch from './pages/PortalSwitch';
import ForcePasswordChange from './pages/ForcePasswordChange';

/** Shown while the server is being asked whether this browser has a session. The
 *  session lives in a cookie the page cannot read, so there is nothing to check
 *  locally — without this gate the first render redirects to /login and an already
 *  signed-in patient loses the page they opened. */
function AuthLoading() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

function PrivateRoute({ children }) {
  const { isAuthenticated, loading, mustChangePassword } = useAuth();

  if (loading) {
    return <AuthLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // A password someone else set has to be replaced before anything else will load.
  if (mustChangePassword) {
    return <ForcePasswordChange />;
  }
  
  return children;
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Toaster position="top-center" />
      <Routes>
        <Route
          path="/login"
          element={loading ? <AuthLoading /> : (isAuthenticated ? <Navigate to="/" replace /> : <Login />)}
        />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/reset" element={<ResetPassword />} />
        {/* Receives a one-time handoff code from another portal's Switch Portal. */}
        <Route path="/switch" element={<PortalSwitch />} />
        <Route 
          path="/" 
          element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="visit/:visitId" element={<VisitDetail />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="clinics" element={<Clinics />} />
          <Route path="health" element={<Health />} />
          <Route path="reports" element={<Reports />} />
          <Route path="appointments" element={<Appointments />} />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
